export type PhoneCountryOption = {
  code: string
  label: string
  digitsMin: number
  digitsMax: number
}

export const PHONE_COUNTRY_OPTIONS: PhoneCountryOption[] = [
  { code: '+91', label: 'India +91', digitsMin: 10, digitsMax: 10 },
  { code: '+1', label: 'US/Canada +1', digitsMin: 10, digitsMax: 10 },
  { code: '+44', label: 'UK +44', digitsMin: 9, digitsMax: 10 },
  { code: '+61', label: 'Australia +61', digitsMin: 9, digitsMax: 9 },
  { code: '+65', label: 'Singapore +65', digitsMin: 8, digitsMax: 8 },
  { code: '+971', label: 'UAE +971', digitsMin: 8, digitsMax: 9 },
  { code: '+81', label: 'Japan +81', digitsMin: 9, digitsMax: 10 },
]

export const DEFAULT_PHONE_COUNTRY_CODE = '+91'

export const digitsOnly = (value: string): string => value.replace(/\D/g, '')

export const getPhoneCountryOption = (code: string): PhoneCountryOption =>
  PHONE_COUNTRY_OPTIONS.find((option) => option.code === code) ?? PHONE_COUNTRY_OPTIONS[0]

export const splitE164Phone = (value: string | null | undefined): { countryCode: string; localNumber: string } => {
  const trimmed = (value ?? '').trim()
  if (!trimmed.startsWith('+')) return { countryCode: DEFAULT_PHONE_COUNTRY_CODE, localNumber: digitsOnly(trimmed) }
  const matching = [...PHONE_COUNTRY_OPTIONS]
    .sort((a, b) => b.code.length - a.code.length)
    .find((option) => trimmed.startsWith(option.code))
  if (!matching) return { countryCode: DEFAULT_PHONE_COUNTRY_CODE, localNumber: digitsOnly(trimmed) }
  return {
    countryCode: matching.code,
    localNumber: digitsOnly(trimmed.slice(matching.code.length)),
  }
}

export const formatE164Phone = (countryCode: string, localNumber: string): string => {
  const option = getPhoneCountryOption(countryCode)
  const digits = digitsOnly(localNumber).slice(0, option.digitsMax)
  return digits ? `${option.code}${digits}` : ''
}

export const isValidSupportedE164Phone = (value: string | null | undefined): boolean => {
  const { countryCode, localNumber } = splitE164Phone(value)
  const option = getPhoneCountryOption(countryCode)
  return localNumber.length >= option.digitsMin && localNumber.length <= option.digitsMax
}

export const maskPhoneNumber = (value: string | null | undefined): string => {
  const trimmed = (value ?? '').trim()
  if (!trimmed) return ''
  const { countryCode, localNumber } = splitE164Phone(trimmed)
  const last4 = localNumber.slice(-4)
  if (!last4) return countryCode ? `${countryCode} •••••` : ''
  return `${countryCode} ••••• ${last4}`
}

export const isPhoneLikeString = (value: string | null | undefined): boolean => {
  if (!value) return false
  const trimmed = value.trim()
  if (!trimmed) return false
  if (/[a-zA-Z]/.test(trimmed)) return false
  const digits = digitsOnly(trimmed)
  return digits.length >= 7
}

export const displayNameOrMaskedPhone = (
  name?: string | null,
  phone?: string | null,
  fallback?: string,
): string => {
  const trimmedName = (name ?? '').trim()
  if (trimmedName && !isPhoneLikeString(trimmedName)) {
    return trimmedName
  }
  const phoneCandidate = (trimmedName && isPhoneLikeString(trimmedName)) ? trimmedName : (phone ?? '').trim()
  if (phoneCandidate) {
    const masked = maskPhoneNumber(phoneCandidate)
    if (masked) return masked
  }
  return fallback ?? ''
}
